import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../../../component/common/designSystem';
import {
  VOLUME_RAMP,
  CADENCE_RAMP,
  SPEED_RAMP,
  STATUS_RAMP,
  COMPLETENESS_RAMP,
  HABIT_RAMP,
  scanColor
} from './scanTheme';

/**
 * One scan's page inside Scans — Skin, Teeth or Age.
 *
 * The top of every page is identical on purpose — take-up, repeat use, how
 * long the analysis takes, when in the week people scan — so an admin comparing
 * two scans is comparing like with like, and only the bottom of the page
 * changes.
 *
 * The bottom is where a scan earns its own page, and in every case it is about
 * the SUBMISSION, not the finding:
 *
 *   Skin   face check versus skin-area check, and how much of the questionnaire
 *          people bothered to fill in
 *   Teeth  which of the six requested photos people actually manage — plus the
 *          two self-reported habit questions
 *   Age    how many of the five readings came back, i.e. whether the model
 *          returned a whole result
 *
 * NOT ONE FIGURE ON THIS PAGE IS A SCAN RESULT. No skin score or risk level,
 * no hygiene score or band, no facial age, no wellbeing reading, no photo and
 * no report link. The endpoint behind the page does not read them.
 */

/** Per-scan copy. The noun matters: "photos" is wrong for an age scan. */
const SCAN_CONFIG = {
  skin: {
    key: 'skin',
    label: 'Skin Scan',
    noun: 'scans',
    peopleTitle: 'People Checking Their Skin',
    volumeTitle: 'Scans per Person',
    emptyMessage: 'Nobody has taken a skin scan yet.'
  },
  teeth: {
    key: 'teeth',
    label: 'Teeth Scan',
    noun: 'scans',
    peopleTitle: 'People Checking Their Teeth',
    volumeTitle: 'Scans per Person',
    emptyMessage: 'Nobody has taken a teeth scan yet.'
  },
  age: {
    key: 'age',
    label: 'Age Scan',
    noun: 'scans',
    peopleTitle: 'People Using Age Scan',
    volumeTitle: 'Scans per Person',
    emptyMessage: 'Nobody has taken an age scan yet.'
  }
};

const ScanPage = ({ scan, fetcher, populationLabel = 'users' }) => {
  const config = SCAN_CONFIG[scan];

  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  if (!config) return <PanelEmpty>Unknown scan.</PanelEmpty>;

  const accent = scanColor(scan);
  const scans = stats?.scans || 0;
  const reliability = stats?.reliability || {};
  const detail = stats?.detail || {};
  const volumeSplit = stats?.volumeSplit || [];
  const cadenceSplit = stats?.cadenceSplit || [];
  const statusSplit = stats?.statusSplit || [];
  const resultTimeSplit = stats?.resultTimeSplit || [];
  const funnel = stats?.funnel || [];
  const weekday = stats?.weekday || [];
  const scansTrend = stats?.scansTrend || [];
  const peopleTrend = stats?.peopleTrend || [];

  const trendHasData = scansTrend.some((value) => value > 0);

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line', stacked: false },
    colors: [accent, CHART_COLORS.moss],
    stroke: { width: [0, 3], curve: 'smooth' },
    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(scansTrend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [accent],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekday.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} ${config.noun}` } }
  };

  /**
   * A `[{label, count, share}]` list as labelled bars, drawn from the share the
   * BACKEND computed rather than a share of the rows on screen.
   *
   * Deliberately not ProgressList, which recomputes each row as a share of the
   * sum of all rows. That is right for a distribution and wrong for both lists
   * here. The funnel's rows are nested subsets — 100 taken, 95 analysed, 93
   * reported — and share-of-sum would print 35% / 33% / 32% for what is really
   * 100% / 95% / 93%. The habit rows do partition their respondents, but their
   * denominator is people who ANSWERED, not people who scanned, and only the
   * backend knows that number.
   */
  const barList = (rows, unit, colorFor = () => accent) => (
    <ul className="space-y-4">
      {rows.map((row, index) => (
        <li key={row.label}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-gray-600 dark:text-gray-300 truncate" title={row.label}>
              {row.label}
            </span>
            <span className="text-sm font-semibold text-shoorah-btn dark:text-white shrink-0">
              {row.share}%
              <span className="ml-1.5 font-normal text-gray-400">
                {formatBigNumber(row.count)} {unit}
              </span>
            </span>
          </div>
          <div className="mt-1.5 h-2 rounded-full bg-shoorah-normanGray dark:bg-shoorah-darkBgColor overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.max(row.share, row.count > 0 ? 2 : 0)}%`,
                backgroundColor: colorFor(index)
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );

  /** A `[{label, count, share}]` list rendered as a table of counts and shares. */
  const shareTable = (rows, firstHeading, countHeading = 'Count') => (
    <PanelTable
      head={
        <>
          <th className="py-2 pr-4 font-medium">{firstHeading}</th>
          <th className="py-2 px-4 font-medium text-center">{countHeading}</th>
          <th className="py-2 font-medium text-right">Share</th>
        </>
      }
    >
      {rows.map((row) => (
        <tr key={row.label} className={PANEL_ROW_CLASS}>
          <td className="py-2 pr-4">{row.label}</td>
          <td className="py-2 px-4 text-center">{formatBigNumber(row.count)}</td>
          <td className="py-2 text-right">{row.share}%</td>
        </tr>
      ))}
    </PanelTable>
  );

  return (
    <div>
      {loader ? <Loader /> : null}

      {/* ---------- Headline: identical on all three pages ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title={config.peopleTitle}
          value={stats ? formatBigNumber(stats.users) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title={`Share of ${populationLabel}`}
          value={stats?.totalUsers > 0 ? `${stats.adoption}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.rating}
          title="Scans Taken"
          value={stats ? formatBigNumber(scans) : '-'}
        />
        {/* Repeat use, not streaks: a scan is not a daily habit, so "did they
            come back at all" is the question worth asking. */}
        <StatCard
          icon={STAT_ICONS.streak}
          title="Came Back"
          value={stats?.users > 0 ? `${stats.repeatRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Typical Result Time"
          value={reliability.avgResultSeconds != null ? `${reliability.avgResultSeconds}s` : '-'}
        />
      </div>

      {/* ---------- Did it work ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <Panel title="From Scan to Report" subtitle="Where a scan gets to, out of every one taken">
          {/* The three steps of one scan. It is the only view that separates
              "nobody used it" from "everybody used it and it broke". */}
          {scans > 0 ? barList(funnel, 'scans') : <PanelEmpty>{config.emptyMessage}</PanelEmpty>}
        </Panel>
        <MiniDonutCard
          title="Analysis Outcome"
          subtitle="Every scan taken, by what happened to it"
          data={statusSplit}
          colors={STATUS_RAMP}
          unit="scans"
          totalLabel="Scans"
          legendLimit={3}
        />
        <MiniDonutCard
          title="Time to Result"
          subtitle="Upload to finished analysis"
          data={resultTimeSplit}
          colors={SPEED_RAMP}
          unit="scans"
          totalLabel="Analysed"
          legendLimit={5}
        />
      </div>

      {/* ---------- Repeat use and rhythm ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title={config.volumeTitle}
          subtitle="How often one person scans"
          data={volumeSplit}
          colors={VOLUME_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={5}
        />
        <MiniDonutCard
          title="Gap Between Scans"
          subtitle="Average, for people who scanned more than once"
          data={cadenceSplit}
          colors={CADENCE_RAMP}
          unit="people"
          totalLabel="Repeat users"
          legendLimit={4}
        />
        <Panel title="When in the Week" subtitle={`${config.label} activity by weekday`}>
          {scans > 0 ? (
            <Chart
              type="bar"
              height={260}
              width="100%"
              series={[{ name: config.label, data: weekday.map((day) => day.count) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>{config.emptyMessage}</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Trend ---------- */}
      <div className="mt-6">
        <Panel title={`${config.label} Over Time`} subtitle="Scans and people — last 30 days">
          {trendHasData ? (
            <Chart
              type="line"
              height={340}
              width="100%"
              series={[
                { name: 'Scans', type: 'column', data: scansTrend },
                { name: 'People', type: 'line', data: peopleTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No scans in the last 30 days.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ================= Scan-specific ================= */}

      {scan === 'skin' && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
          <MiniDonutCard
            title="Which Check People Run"
            subtitle="A whole-face scan, or one patch of skin"
            data={detail.modeSplit || []}
            colors={[CHART_COLORS.orange, CHART_COLORS.blue, CHART_COLORS.gray]}
            unit="scans"
            totalLabel="Scans"
            legendLimit={3}
          />
          <MiniDonutCard
            title="Questionnaire Completed"
            subtitle="How many of the four questions people answered"
            data={detail.questionnaireSplit || []}
            colors={COMPLETENESS_RAMP}
            unit="scans"
            totalLabel="Scans"
            legendLimit={5}
          />
          {/* Completeness only. What the answers SAY is deliberately absent:
              three of the four are symptom questions ("new moles or spots in
              the past three months"), and all four are free text the client
              posts unvalidated. */}
        </div>
      )}

      {scan === 'teeth' && (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
            <Panel
              title="Photos Captured"
              subtitle="Which of the six angles people actually manage"
            >
              {/* The most actionable thing this scan produces, and it says
                  nothing about anybody's teeth: if the lower jaw is the view
                  everyone skips, that is a camera-guidance problem. Shares are
                  of all scans and overlap by design, so this is a table rather
                  than bars scaled against their own sum. */}
              {scans > 0 ? (
                shareTable(detail.perView || [], 'View', 'Scans with it')
              ) : (
                <PanelEmpty>{config.emptyMessage}</PanelEmpty>
              )}
            </Panel>
            <MiniDonutCard
              title="How Complete a Submission Is"
              subtitle="Photos supplied per scan, out of six"
              data={detail.viewSplit || []}
              colors={COMPLETENESS_RAMP}
              unit="scans"
              totalLabel="Scans"
              legendLimit={5}
            />
          </div>

          {/* The one place in the section where something people said about
              themselves is reported. Self-reported behaviours on a fixed
              scale — not an AI's reading of a photo — aggregate-only, and
              withheld by the backend below a minimum cohort. */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
            {(detail.habits || []).map((habit) => (
              <Panel key={habit.key} title={habit.title} subtitle="Self-reported, aggregate only">
                {habit.suppressed ? (
                  <PanelEmpty>
                    {habit.respondents > 0
                      ? `Withheld — only ${habit.respondents} ${
                          habit.respondents === 1 ? 'person has' : 'people have'
                        } answered this. At least ${
                          habit.minimum
                        } are needed before it can be shown, so answers can't be traced back to individuals.`
                      : 'Nobody has answered this question yet.'}
                  </PanelEmpty>
                ) : (
                  // Best answer first, so the ramp runs green → red down the
                  // list. Red here describes what somebody said about their own
                  // habits, not a finding about their mouth.
                  barList(
                    habit.distribution,
                    'people',
                    (index) => HABIT_RAMP[index] || HABIT_RAMP[HABIT_RAMP.length - 1]
                  )
                )}
              </Panel>
            ))}
          </div>

          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
            Gum bleeding, tooth sensitivity and smoking are also asked in the app. They are not
            reported here — the first two are symptoms rather than habits, and the third is a
            personal matter.
          </p>
        </>
      )}

      {scan === 'age' && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
          <MiniDonutCard
            title="Readings Returned"
            subtitle="How many of the five the model produced"
            data={detail.readingSplit || []}
            colors={COMPLETENESS_RAMP}
            unit="scans"
            totalLabel="Scans"
            legendLimit={4}
          />
          <Panel
            title="Which Readings Came Back"
            subtitle="Whether the model produced each one — never what it said"
          >
            {/* Presence, not value. The reading itself — stress, fatigue,
                hydration, mood, and the facial-age estimate behind them — went
                to the person who took the scan and stays with them. */}
            {scans > 0 ? (
              shareTable(detail.perReading || [], 'Reading', 'Scans with it')
            ) : (
              <PanelEmpty>{config.emptyMessage}</PanelEmpty>
            )}
          </Panel>
          <p className="xl:col-span-2 text-xs text-gray-400 dark:text-gray-500">
            The age estimate itself is not reported at any level of aggregation. It is a judgement
            about how old somebody looks, and it belongs to them.
          </p>
        </div>
      )}
    </div>
  );
};

ScanPage.propTypes = {
  scan: PropTypes.string,
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default ScanPage;
